import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, MapPin } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECORD_LABELS = {
  clock_in: 'Entrada',
  clock_out: 'Salida',
  break_start: 'Inicio de pausa',
  break_end: 'Fin de pausa',
};

const useQuery = () => new URLSearchParams(window.location.search);

export default function QrClock() {
  const q = useQuery();
  const orgId = q.get('org');
  const locationId = q.get('loc');
  const token = q.get('t');

  const [status, setStatus] = useState('locating'); // locating | sending | success | error
  const [message, setMessage] = useState('Obteniendo tu ubicación…');
  const [result, setResult] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // evita doble ejecución en StrictMode
    ran.current = true;

    if (!orgId || !locationId || !token) {
      setStatus('error');
      setMessage('Código QR inválido o incompleto.');
      return;
    }

    const submit = (location) => {
      setStatus('sending');
      setMessage('Registrando tu fichaje…');
      axios.post(`${API_URL}/qr/clock`, { location_id: locationId, token, location })
        .then((res) => {
          setStatus('success');
          setResult(res.data);
        })
        .catch((e) => {
          setStatus('error');
          setMessage(e.response?.data?.detail || 'No se pudo registrar el fichaje.');
        });
    };

    if (!navigator.geolocation) {
      setStatus('error');
      setMessage('Tu dispositivo no permite geolocalización, necesaria para fichar.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => submit({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => {
        setStatus('error');
        setMessage('Necesitas permitir el acceso a la ubicación para fichar por QR.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [orgId, locationId, token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
        {(status === 'locating' || status === 'sending') && (
          <>
            <Loader2 className="w-14 h-14 text-indigo-600 mx-auto animate-spin" />
            <p className="mt-4 text-slate-700 font-medium">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">
              {RECORD_LABELS[result?.record_type] || 'Fichaje'} registrada
            </h1>
            <p className="mt-1 text-slate-600">{result?.employee_name}</p>
            <p className="mt-2 text-3xl font-bold text-indigo-600 tabular-nums">
              {result?.timestamp ? new Date(result.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
            </p>
            <p className="mt-4 text-sm text-slate-400">Ya puedes cerrar esta ventana.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">No se pudo fichar</h1>
            <p className="mt-2 text-slate-600 flex items-center justify-center gap-1">
              <MapPin className="w-4 h-4 flex-shrink-0" /> {message}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-5 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Reintentar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
